/**
 * A schedule's Status cell — Himaad's Section 5 gives this column its own slot,
 * which the card layout did not have.
 *
 * ## Needs sign-in WINS over both Active and Paused
 *
 * `enabled` and `needsSignIn` are INDEPENDENT fields and both can be set at once.
 * A schedule whose stored credential has lapsed keeps `enabled = 1` — nobody
 * turned it off — and yet the cron skips it, so it will not fire.
 *
 * Deriving this cell from `enabled` alone would therefore print **"Active"** over
 * a schedule that is silently not running. That is precisely the failure the
 * whole credential design exists to prevent: silently not running is what costs
 * someone a month-end, and the Queue page's chip already counts the two states
 * separately on purpose.
 *
 * So the precedence is: **needs sign-in → paused → active**. The owner has to act
 * on a dormant credential; owner-paused is a state they chose.
 *
 * ## A non-owner sees only Active or Paused
 *
 * Dormancy is suppressed on someone else's row, for two reasons. It is not theirs
 * to fix — every mutating route is owner-scoped — and it discloses a colleague's
 * credential state, which is not information a shared view needs to carry. They
 * see the cadence and whether it is meant to be running.
 */
const TONES = {
  active: 'bg-emerald-50 text-emerald-800 ring-emerald-600/20',
  paused: 'bg-amber-100 text-amber-800 ring-amber-600/20',
  dormant: 'bg-red-50 text-rc-danger-text ring-red-600/20',
}

const ScheduleStatusCell = ({ schedule: s, isOwner = true }) => {
  /*
   * `isOwner &&` is now BELT AND BRACES, not the load-bearing guard.
   *
   * Since 2026-09-03 the API withholds `needsSignIn` on a foreign row (it forces
   * `false`), so this component can no longer receive a colleague's dormancy to
   * suppress — the suppression moved to where it belongs, upstream of the browser,
   * because a field the client never receives cannot be inferred from. Two sibling
   * cells in this table WERE inferring it.
   *
   * Kept rather than simplified away: it is one `&&`, it keeps this component's
   * stated contract true when read on its own, and it holds against a payload from
   * an older worker that still sends the field. Do not "clean it up" — the
   * defence is cheap and its absence would be silent.
   */
  const dormant = isOwner && !!s.needsSignIn
  const tone = dormant ? 'dormant' : s.enabled ? 'active' : 'paused'
  const label = dormant ? 'Needs sign-in' : s.enabled ? 'Active' : 'Paused'

  return (
    <span className="inline-flex flex-col items-start gap-0.5">
      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${TONES[tone]}`}>
        {label}
      </span>
      {/*
        * The dormancy explanation stays LOUD and stays next to the status, rather
        * than being compressed into the chip. It tells the owner what to DO,
        * which a status word cannot.
        */}
      {dormant && (
        <span className="text-[11px] leading-tight text-rc-danger-text">
          Sign in again to restart it
        </span>
      )}
    </span>
  )
}

export default ScheduleStatusCell
